import React from 'react';

import { type Command, type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../editor-plugin-ai';
import { type Recommendation } from '../pm-plugins/ai-proactive/api';
import {
	clearHoverRecommendation,
	hoverRecommendation,
	insertRecommendation,
	removeRecommendation,
	selectRecommendation,
	toggleProactiveAISuggestionDisplay,
} from '../pm-plugins/ai-proactive/commands';
import { getPluginState as getProactivePluginState } from '../pm-plugins/ai-proactive/plugin-factory';
import { getAllRecommendations } from '../pm-plugins/ai-proactive/utils';
import { getPluginState as getSpellingGrammarPluginState } from '../pm-plugins/ai-spelling-grammar/plugin-factory';
import { getAllDiffObjects } from '../pm-plugins/ai-spelling-grammar/utils';
import { getPluginState as getDecorationPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';

import { RecommendationPanel } from './recommendation-panel';

export const getContextPanel =
	(getEditorView?: () => EditorView | undefined) =>
	(api: ExtractInjectionAPI<AIPlugin> | undefined) =>
	(state: EditorState) => {
		const proactivePluginState = getProactivePluginState(state);
		const spellingGrammarPluginState = getSpellingGrammarPluginState(state);
		if (proactivePluginState) {
			const {
				isProactiveEnabled,
				isProactiveContextPanelOpen,
				selectedRecommendationId,
				hoveredRecommendationId
			} = proactivePluginState;
			if (isProactiveEnabled && isProactiveContextPanelOpen) {
				const decorationPluginState = getDecorationPluginState(state);
				if (decorationPluginState.modalDecorationSet === DecorationSet.empty) {
					const recommendations = getAllRecommendations(proactivePluginState);

					if (!recommendations) {
						return null;
					}

					const triggerLocation = 'contextPanel';
					const runWithStateAndDispatch = (command: Command) => {
						const editorView = getEditorView?.();
						if (!editorView) {
							return;
						}
						const { state, dispatch } = editorView;
						command(state, dispatch);
					};

					const onRecommendationApply = ({recommendation}: {recommendation: Recommendation}) => {
						runWithStateAndDispatch(
							insertRecommendation(
								recommendation.id,
								triggerLocation,
								api?.contextPanel?.actions.applyChange,
							)
						);
					};

					const onRecommendationDismiss = ({recommendation}: {recommendation: Recommendation}) => {
						runWithStateAndDispatch(
							removeRecommendation(
								recommendation.id,
								triggerLocation,
								api?.contextPanel?.actions.applyChange,
							)
						);
					};

					const onRecommendationSelect = ({recommendation}: {recommendation: Recommendation}) => {
						runWithStateAndDispatch(selectRecommendation(recommendation.id, api?.contextPanel?.actions.applyChange));
					};

					const onRecommendationHover = ({recommendation}: {recommendation: Recommendation}) => {
						runWithStateAndDispatch(hoverRecommendation(recommendation.id, api?.contextPanel?.actions.applyChange));
					};

					const onRecommendationLeave = ({ recommendation }: { recommendation: Recommendation }) => {
						runWithStateAndDispatch(clearHoverRecommendation(api?.contextPanel?.actions.applyChange));
					};

					const onPanelClose = () => {
						runWithStateAndDispatch(toggleProactiveAISuggestionDisplay(triggerLocation));
					};

					const onSpellingGrammar = () => {
						// FIXME: Trigger next spelling and grammar suggestion.
					};

					const isSpellingGrammarEnabled = spellingGrammarPluginState?.isSpellingAndGrammarEnabled ?? false;
					const spellingGrammarErrorCount = getAllDiffObjects(spellingGrammarPluginState ?? {})?.length ?? 0;

					return (
						<RecommendationPanel
							isSpellingGrammarEnabled={isSpellingGrammarEnabled}
							spellingGrammarErrorCount={spellingGrammarErrorCount}
							recommendations={recommendations}
							hoveredRecommendationId={hoveredRecommendationId}
							selectedRecommendationId={selectedRecommendationId}
							onRecommendationApply={onRecommendationApply}
							onRecommendationDismiss={onRecommendationDismiss}
							onRecommendationSelect={onRecommendationSelect}
							onRecommendationHover={onRecommendationHover}
							onRecommendationLeave={onRecommendationLeave}
							onSpellingGrammar={onSpellingGrammar}
							onPanelClose={onPanelClose}
						/>
					);
				}
			}
			return null;
		}
	};
